import {
    useCreateCartItemMutation,
    useCreateSelfcareCartItemMutation,
    useDeleteCartItemByIdMutation,
    useDeleteCartItemsByIdMutation,
    useDeleteSelfcareCartItemByIdMutation,
    useDeleteSelfcareCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    removeUpsell,
    setUpsellQuantity,
    updateUpsell,
    UpsellType,
} from 'store/upsell-slice'
import { handleError } from 'helpers/errors'
import { useApp } from 'store/steps'
import { useCart } from 'store/cart-slice/hooks'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { useEffect, useState } from 'react'

interface UseUpsellProps {
    type?: UpsellType
    productId?: string
}

export const useUpsell = ({
    type,
    productId,
}: UseUpsellProps): {
    removeUpsellFromCart: () => Promise<void>
    removeAllUpsellDataFromCart: () => Promise<void>
    editUpsellInCart: (quantity: number) => Promise<void>
    isUpsellProcessing: boolean
} => {
    const dispatch = useAppDispatch()
    const creatorCartItems = useAppSelector((state) => state.cart.items)
    const selfcareCartItems = useAppSelector(
        (state) => state.selfcareCart.items
    )
    const upsells = useAppSelector((state) => state.upsell)
    const currentUpsell = type ? upsells[type] : {}
    const { language, isCreatorMode } = useApp()
    const cartItems = isCreatorMode ? creatorCartItems : selfcareCartItems
    const cartUpsell = type ? cartItems[type] : {}
    const cartMemoryCaptureId = cartItems.memoryCapture?.cartItemId || ''

    // CREATOR
    const { refetchCart: refetchCreatorCart, cartCreatorType } = useCart()
    const [deleteCreatorCartItem, deleteStatus] =
        useDeleteCartItemByIdMutation()
    const [deleteCreatorCartItems, deleteCreatorItemsStatus] =
        useDeleteCartItemsByIdMutation()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [postCartItem, postStatus] = useCreateCartItemMutation()

    // SELFCARE
    const { refetchSelfcareCart } = useSelfcareCart()
    const [deleteSelfcareCartItem, deleteSelfcareStatus] =
        useDeleteSelfcareCartItemByIdMutation()
    const [deleteSelfcareCartItems, deleteSelfcareItemsStatus] =
        useDeleteSelfcareCartItemsByIdMutation()
    const [postSelfcareCartItem, postSelfcareStatus] =
        useCreateSelfcareCartItemMutation()

    const refetchCart = isCreatorMode ? refetchCreatorCart : refetchSelfcareCart

    const isUpsellLoading = isCreatorMode
        ? postStatus.isLoading ||
          deleteStatus.isLoading ||
          deleteCreatorItemsStatus.isLoading
        : postSelfcareStatus.isLoading ||
          deleteSelfcareStatus.isLoading ||
          deleteSelfcareItemsStatus.isLoading

    const [isUpsellEditProcessing, setIsUpsellEditProcessing] = useState(false)
    const [isUpsellProcessing, setIsUpsellProcessing] = useState(
        isUpsellEditProcessing || isUpsellLoading
    )

    useEffect(() => {
        setIsUpsellProcessing(isUpsellEditProcessing || isUpsellLoading)
    }, [isUpsellEditProcessing, isUpsellLoading])

    const removeUpsellFromCart = async () => {
        if (!type || !cartUpsell?.cartItemId) return

        const params = {
            cartItemId: cartUpsell.cartItemId,
            _locale: language,
        }

        if (isCreatorMode) {
            deleteCreatorCartItem({
                ...params,
                cartCreatorType,
            }).then((data) => {
                handleError(data)

                dispatch(removeUpsell(type))
                refetchCart()
            })
        } else {
            deleteSelfcareCartItem({
                ...params,
            }).then((data) => {
                handleError(data)

                dispatch(removeUpsell(type))
                refetchCart()
            })
        }
    }

    const removeAllUpsellDataFromCart = async () => {
        if (!cartMemoryCaptureId) return

        const ticketsIdsToRemove = []

        cartMemoryCaptureId && ticketsIdsToRemove.push(cartMemoryCaptureId)

        if (isCreatorMode) {
            await deleteCreatorCartItems({
                _locale: language,
                deleteCartItemsByIdCommand: {
                    cartItemUuids: ticketsIdsToRemove,
                },
                cartCreatorType,
            })
        } else {
            await deleteSelfcareCartItems({
                _locale: language,
                deleteCartItemsByIdSelfcareCommand: {
                    cartItemUuids: ticketsIdsToRemove,
                },
            })
        }
        refetchCart()
        dispatch(removeUpsell('memoryCapture'))
    }

    const addUpsellToCart = async (quantity: number) => {
        if (!currentUpsell || !type) return

        const { cartItemId, productType } = currentUpsell
        if (!cartItemId || !productId || !productType) return

        if (isCreatorMode) {
            // TODO: uncomment and fix cart product types if memory ever come back
            // await postCartItem({
            //     createCartItemCommand: {
            //         cartItemId,
            //         productId,
            //         productType,
            //         quantity,
            //     },
            //     cartCreatorType,
            //     _locale: language,
            // })
        } else {
            await postSelfcareCartItem({
                createCartItemSelfcareCommand: {
                    cartItemId,
                    productId,
                    productType,
                    quantity,
                },
                _locale: language,
            })
        }

        dispatch(
            setUpsellQuantity({
                quantity,
                type,
            })
        )

        refetchCart(true)
    }

    const editUpsellInCart = async (quantity: number) => {
        if (!currentUpsell || !type) return

        const { cartItemId, productType } = currentUpsell
        if (!cartItemId || !productId || !productType) return

        if (cartUpsell?.cartItemId) {
            setIsUpsellEditProcessing(true)

            const params = {
                cartItemId: cartUpsell.cartItemId,
                _locale: language,
            }

            if (isCreatorMode) {
                deleteCreatorCartItem({
                    ...params,
                    cartCreatorType,
                })
            } else {
                deleteSelfcareCartItem({
                    ...params,
                })
            }

            if (isCreatorMode) {
                // TODO: uncomment and fix cart product types if memory ever come back
                // await postCartItem({
                //     createCartItemCommand: {
                //         cartItemId,
                //         productId,
                //         productType,
                //         quantity,
                //     },
                //     cartCreatorType,
                //     _locale: language,
                // })
            } else {
                await postSelfcareCartItem({
                    createCartItemSelfcareCommand: {
                        cartItemId,
                        productId,
                        productType,
                        quantity,
                    },
                    _locale: language,
                })
            }

            dispatch(
                updateUpsell({
                    type,
                    quantity,
                })
            )
            refetchCart()
            setIsUpsellEditProcessing(false)
        } else {
            await addUpsellToCart(quantity)
        }
    }

    return {
        removeUpsellFromCart,
        removeAllUpsellDataFromCart,
        editUpsellInCart,
        isUpsellProcessing,
    }
}
